import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState, useRef, useEffect } from "react";
import { Menu, X, ChevronDown } from "lucide-react";

const VVLogo = ({ size = 36 }) => (
  <img src="/vv-logo-mark.png" alt="VV" width={size} height={size} className="rounded-full" />
);

const navLinks = [
  { label: "Home", to: "/" },
  {
    label: "About",
    to: "/about",
    dropdown: [
      { label: "About Us", to: "/about" },
      { label: "Meet Our Team", to: "/meet-our-team" },
    ],
  },
  { label: "Projects", to: "/projects" },
  { label: "Join Us", to: "/join-us" },
];

const socialLinks = [
  { label: "Facebook", href: "https://www.facebook.com/virtually.verbal", icon: "facebook" },
  { label: "Instagram", href: "https://www.instagram.com/virtuallyverbal/", icon: "instagram" },
  { label: "Twitter", href: "https://twitter.com/officiallyVV", icon: "twitter" },
];

const SocialIcon = ({ type }) => {
  if (type === "facebook") return (
    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
  );
  if (type === "instagram") return (
    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
  );
  if (type === "twitter") return (
    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
  );
  return null;
};

export const MarketingNav = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setDropdownOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  return (
    <header className="fixed top-0 left-0 right-0 z-50 border-b-2 border-[#0A0A0A]" style={{ background: "rgba(253,251,247,0.95)", backdropFilter: "blur(8px)" }}>
      {/* Announcement bar */}
      <div className="bg-[#3C8DE4] text-white text-center text-xs sm:text-sm font-semibold py-1.5 px-4">
        ESL Spanish classes in partnership with <span className="font-bold">Schools for Children of the World</span> have begun!
      </div>
      
      <div className="container mx-auto px-6 py-3 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5 shrink-0" data-testid="logo-link">
          <VVLogo size={36} />
          <span className="font-bold text-lg tracking-tight">Virtually Verbal</span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-6">
          {navLinks.map((link) =>
            link.dropdown ? (
              <div key={link.to} className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  className={`text-sm font-semibold transition-colors hover:text-[#3C8DE4] flex items-center gap-1 ${location.pathname === link.to || location.pathname === "/meet-our-team" ? "text-[#3C8DE4]" : "text-[#0A0A0A]"}`}
                  data-testid="nav-about-dropdown"
                >
                  {link.label}
                  <ChevronDown className={`w-3.5 h-3.5 transition-transform ${dropdownOpen ? "rotate-180" : ""}`} />
                </button>
                {dropdownOpen && (
                  <div className="absolute top-full left-0 mt-2 w-44 bg-white border-2 border-[#0A0A0A] rounded-lg overflow-hidden" style={{ boxShadow: "4px 4px 0px 0px rgba(10,10,10,1)" }}>
                    {link.dropdown.map((item) => (
                      <Link
                        key={item.to}
                        to={item.to}
                        onClick={() => setDropdownOpen(false)}
                        className={`block px-4 py-2.5 text-sm font-semibold hover:bg-[#D6E6FA] transition-colors ${location.pathname === item.to ? "text-[#3C8DE4] bg-[#D6E6FA]/50" : "text-[#0A0A0A]"}`}
                        data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
                      >
                        {item.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <Link
                key={link.to}
                to={link.to}
                className={`text-sm font-semibold transition-colors hover:text-[#3C8DE4] ${location.pathname === link.to ? "text-[#3C8DE4]" : "text-[#0A0A0A]"}`}
                data-testid={`nav-${link.label.toLowerCase().replace(/\s/g, "-")}`}
              >
                {link.label}
              </Link>
            )
          )}
        </nav>

        {/* Desktop auth */}
        <div className="hidden md:flex items-center gap-3">
          <a href="https://www.zeffy.com/en-US/donation-form/the-global-classroom-campaign" target="_blank" rel="noopener noreferrer" className="text-sm font-semibold text-[#E05A73] hover:text-[#C04050] transition-colors" data-testid="donate-nav-btn">Donate</a>
          <button onClick={() => navigate("/tutor/login")} className="text-sm font-semibold text-[#4A4A4A] hover:text-[#0A0A0A] transition-colors" data-testid="tutor-login-nav-btn">Tutor</button>
          <button onClick={() => navigate("/login")} className="brutalist-btn-primary text-xs !px-5 !py-2" data-testid="student-login-btn">Student Login</button>
        </div>

        {/* Mobile hamburger */}
        <button className="md:hidden p-2" onClick={() => setMobileOpen(!mobileOpen)} data-testid="mobile-menu-toggle">
          {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="md:hidden border-t-2 border-[#0A0A0A] bg-[#FDFBF7] px-6 py-4 space-y-3">
          <Link to="/" onClick={() => setMobileOpen(false)} className={`block text-sm font-semibold py-2 ${location.pathname === "/" ? "text-[#3C8DE4]" : "text-[#0A0A0A]"}`}>Home</Link>
          <Link to="/about" onClick={() => setMobileOpen(false)} className={`block text-sm font-semibold py-2 ${location.pathname === "/about" ? "text-[#3C8DE4]" : "text-[#0A0A0A]"}`}>About Us</Link>
          <Link to="/meet-our-team" onClick={() => setMobileOpen(false)} className={`block text-sm font-semibold py-2 pl-4 ${location.pathname === "/meet-our-team" ? "text-[#3C8DE4]" : "text-[#4A4A4A]"}`}>Meet Our Team</Link>
          <Link to="/projects" onClick={() => setMobileOpen(false)} className={`block text-sm font-semibold py-2 ${location.pathname === "/projects" ? "text-[#3C8DE4]" : "text-[#0A0A0A]"}`}>Projects</Link>
          <Link to="/join-us" onClick={() => setMobileOpen(false)} className={`block text-sm font-semibold py-2 ${location.pathname === "/join-us" ? "text-[#3C8DE4]" : "text-[#0A0A0A]"}`}>Join Us</Link>
          <hr className="border-[#0A0A0A]/20" />
          <div className="flex flex-col gap-2 pt-1">
            <a href="https://www.zeffy.com/en-US/donation-form/the-global-classroom-campaign" target="_blank" rel="noopener noreferrer" className="text-sm font-semibold text-left py-1 text-[#E05A73]">Donate</a>
            <button onClick={() => { navigate("/tutor/login"); setMobileOpen(false); }} className="text-sm font-semibold text-left py-1">Tutor Login</button>
            <button onClick={() => { navigate("/login"); setMobileOpen(false); }} className="brutalist-btn-primary text-xs !px-5 !py-2 w-fit mt-1">Student Login</button>
          </div>
        </div>
      )}
    </header>
  );
};

export const MarketingFooter = () => {
  return (
    <footer className="bg-[#0A0A0A] text-white pt-16 pb-8 px-6">
      <div className="container mx-auto">
        <div className="grid md:grid-cols-4 gap-10 mb-12">
          {/* Brand */}
          <div className="md:col-span-2">
            <div className="flex items-center gap-2.5 mb-4">
              <VVLogo size={36} />
              <span className="font-bold text-lg">Virtually Verbal</span>
            </div>
            <p className="text-white/60 text-sm leading-relaxed max-w-sm">
              Connecting learners & volunteers worldwide through free online language education
            </p>
            <div className="flex items-center gap-4 mt-5">
              {socialLinks.map((s) => (
                <a key={s.icon} href={s.href} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center hover:bg-[#3C8DE4] transition-colors" data-testid={`footer-social-${s.icon}`}>
                  <SocialIcon type={s.icon} />
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-bold text-sm uppercase tracking-wider mb-4 text-white/40">Quick Links</h4>
            <ul className="space-y-2.5">
              {navLinks.map((link) => (
                <li key={link.to}>
                  <Link to={link.to} className="text-sm text-white/70 hover:text-white transition-colors">{link.label}</Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Connect */}
          <div>
            <h4 className="font-bold text-sm uppercase tracking-wider mb-4 text-white/40">Connect</h4>
            <a href="mailto:officialvirtuallyverbal@gmail.com" className="text-sm text-white/70 hover:text-white transition-colors break-all" data-testid="footer-email">
              officialvirtuallyverbal@gmail.com
            </a>
          </div>
        </div>

        <div className="border-t border-white/10 pt-6 text-center">
          <p className="text-xs text-white/40">&copy; 2026 Virtually Verbal. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
};

const MarketingLayout = ({ children }) => {
  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <MarketingNav />
      <main className="pt-[88px]">{children}</main>
      <MarketingFooter />
    </div>
  );
};

export default MarketingLayout;
